import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import classNames from 'classnames';
import FormContent from 'components/layout/FormContent';
import LoaderComponent from 'components/LoaderComponent';
import _ from 'lodash';
import React from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Header, Icon, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { InvoiceStatistic2Request, InvoiceStatisticGroupData, TariffDto } from 'ts-types/api.types';
import { isoToDisplayDate } from 'utils/dateUtils';
import { numberFormatter } from 'utils/fomattingUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  position: relative;
`;

const StyledInvoiceStatistic = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;

  .bill-list-form {
    display: flex;
    flex-direction: column;
  }

  .ui.table tr td.group {
    border-right: 1px solid #777;
  }

  .ui.table tr.level-header.level-header-1 td {
    border-top: 2px solid #777;
  }

  .ui.table tr.level-header.level-header-2 td {
    border-top: 1px solid #777;
  }

  .ui.table tr.interrupted-row td {
    font-style: italic;
    color: #3a93db;
  }

  .ui.table tr.vat-rate-total-row td {
    font-weight: bold;
    background: #f7f7f7;
  }

  .ui.table tr.year-row.tariff-row td {
    border-top: 2px solid #777;
  }

  .ui.table tr.tariff-row td.year-cell {
    border-right: 1px solid #777;
  }

  .ui.table tr.tariff-row td.tariff-cell {
    border-right: 1px solid #777;
  }

  .ui.table tr th .header-content {
    display: inline-block;
    max-width: 120px;
  }
`;

const StyledFormContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  invoiceStatisticData: InvoiceStatisticGroupData,
  groups: string[],
  searchRequest: InvoiceStatistic2Request,
  dataLoading: boolean,
  tariffs: TariffDto[],
  setErrorMessage: (errorMessage?: string) => void
}

interface RowData {
  cells: CellData[];
  groups: { [key: string]: string };
  levelHeader?: number;
}

class CellData {

  level: number = 0;
  rowSpan: number = 1;
  value: JSX.Element = <></>;
  groupCell = true;

  constructor(level: number, rowSpan: number, value: JSX.Element) {
    this.level = level;
    this.rowSpan = rowSpan;
    this.value = value;
  }

}

const InvoiceStatistic2bTableSegment = (props: Props) => {

  const {
    invoiceStatisticData,
    groups,
    searchRequest,
    dataLoading,
    getMessage
  } = props;

  const cell = (rowSpan: number, value: number): CellData => {
    const cellData = new CellData(
        0,
        rowSpan,
        <>{numberFormatter.format(value)}</>
    );
    cellData.groupCell = false;
    return cellData;
  };

  const showGroupColumn = (group: string): boolean => !["interruptedz"].includes(group);

  const getRowSpan = (groupStats: InvoiceStatisticGroupData): number => {
    if (!groupStats.group) {
      return 1;
    }
    return _.values(groupStats.groupsMap)
        .map(childGroup => getRowSpan(childGroup))
        .reduce((prev, val) => prev + val, 0);
  }

  const renderGroupRows = (
      level: number,
      currentRow: RowData,
      groupStats: InvoiceStatisticGroupData): RowData[] => {

    if (groupStats.group) {
      let groupVals = _.keys(groupStats.groupsMap);
      if (groupVals.length === 0) {
        return [];
      }

      if (groupStats.groupByParameter.startsWith("period")
          || groupStats.groupByParameter.startsWith("tariff")) {
        groupVals.sort();
      } else if (groupStats.groupByParameter.startsWith("vat-rate")) {
        groupVals.sort((a, b) => {
          if (a === "total") {
            return 1;
          }
          if (b === "total") {
            return -1;
          }
          return (+a) - (+b);
        });
      }

      return groupVals.map((val, ix) => {
            const newParents = ix == 0 ? [...currentRow.cells] : [];

            if (showGroupColumn(groupStats.groupByParameter)) {

              let nodeVal = <>{val}</>;
              if (groupStats.groupByParameter == "tariff") {
                const tariff = props.tariffs.find(t => t.id === +val);
                nodeVal = tariff ? <>{tariff.description}</> : nodeVal;
              } else if (groupStats.groupByParameter == "interrupted") {
                nodeVal = val === "0" ? <></> : <Icon name="circle" color="blue"/>;
              } else if (groupStats.groupByParameter == "vat-rate") {
                nodeVal = val === "total" ? <>{getMessage("common.total")}</> : <>{val}%</>;
              }

              newParents.push(new CellData(
                  currentRow.cells.length, getRowSpan(groupStats.groupsMap[val]), nodeVal));
            }

            const groups = {
              ...currentRow.groups,
              [groupStats.groupByParameter]: val
            };

            let levelHeader = currentRow.levelHeader;
            if (ix != 0) {
              levelHeader = undefined;
            } else if (!levelHeader) {
              levelHeader = level;
            }

            return renderGroupRows(
                level + 1,
                {cells: newParents, groups, levelHeader},
                groupStats.groupsMap[val]);
          })
          .flat();
    }

    const stats = groupStats.data;
    return [{
      groups: currentRow.groups,
      cells: [
        ...currentRow.cells,
        cell(1, stats.totalInvoiced + stats.totalNotInvoiced),
        cell(1, stats.totalInvoiced),
        cell(1, stats.totalNotInvoiced),
        cell(1, stats.totalPaid),
        cell(1, stats.totalInvoiced - stats.totalPaid),
        cell(1, stats.totalNotInvoicedOpen),
        cell(1, stats.totalStorno)
      ],
      levelHeader: currentRow.levelHeader
    }];
  }

  const renderInvoiceStatisticTable = (): React.ReactNode => {

    return (
        <Table compact="very">
          <Table.Header>
            <Table.Row>
              {
                groups.filter(showGroupColumn).map(group => {
                  if (group === "interrupted") {
                    return <Table.HeaderCell style={{width: "10px"}}>
                      <Icon name="flag"
                            title={getMessage(`invoiceStatistic2.searchSection.groupBy.${group}`)}
                      />
                    </Table.HeaderCell>;
                  }
                  return <Table.HeaderCell>{getMessage(`invoiceStatistic2.searchSection.groupBy.${group}`)}</Table.HeaderCell>;
                })
              }
              <Table.HeaderCell textAlign="right">{getMessage("invoiceStatistic2.revenue")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{getMessage("invoiceStatistic2.invoiced")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{getMessage("invoiceStatistic2.notInvoiced")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{getMessage("invoiceStatistic2.paid")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{getMessage("invoiceStatistic2.notPaid")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{getMessage("invoiceStatistic2.notInvoicedOpen")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{getMessage("invoiceStatistic2.storno")}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {
              renderGroupRows(0, {cells: [], groups: {}}, invoiceStatisticData).map(row => (
                  <Table.Row
                      className={classNames({
                        ["level-header"]: row.levelHeader != undefined,
                        ["level-header-" + row.levelHeader]: row.levelHeader != undefined,
                        "interrupted-row": row.groups["interrupted"] === "1",
                        "vat-rate-total-row": row.groups["vat-rate"] === "total"
                      })}
                  >
                    {
                      row.cells.map(cell => (
                          <Table.Cell
                              textAlign="right"
                              rowSpan={cell.rowSpan}
                              className={classNames({
                                ["group"]: cell.groupCell,
                                ["group-" + cell.level]: cell.groupCell
                              })}
                          >
                            {cell.value}
                          </Table.Cell>
                      ))
                    }
                  </Table.Row>
              ))
            }
          </Table.Body>
        </Table>
    )
  };

  const title = getMessage("header.navigation.invoiceStatistic");
  const headerMessage = dataLoading
      ? title
      : `${title} (${searchRequest.detailedSearch ? isoToDisplayDate(searchRequest.fromDate) : ""} - ${isoToDisplayDate(searchRequest.toDate)})`

  return (
      <StyledInvoiceStatistic>
        <FormContent className="bill-list-form">
          <StyledFormContent>
            <StyledContainer style={{width: "100%"}}>
              <Header>
                {headerMessage}
              </Header>
              <TableContainer>
                {dataLoading
                    ? <LoaderComponent
                        message={`${getMessage("loading")}
                          ${getMessage("header.navigation.invoiceStatistic").toLowerCase()}`}/>
                    : renderInvoiceStatisticTable()}
              </TableContainer>
            </StyledContainer>
          </StyledFormContent>
        </FormContent>
      </StyledInvoiceStatistic>
  );
}

export default withAuthContext(withRouterWorkaround(withMessages(InvoiceStatistic2bTableSegment)));
